import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { SettingModelSettingType } from 'api/models';
import { useSolveAnchorPlacement } from 'api/queries/forming/forming';
import { activateManualSnapPlacement } from 'app/modules/wallforming/components/scenes/objects/articles/utils/manualSnapPlacementUtils';
import { buildSolveAnchorPlacementRequest } from 'app/modules/wallforming/components/setting-container/anchor-placement/utils/buildSolveAnchorPlacementRequest';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { EMPTY_GUID } from 'shared/constants';
import { toastNotifications } from 'shared/utils/toastNotifications';

export interface ManualAnchorPlacementParams {
    articleId: string;
    poiIndex: number;
}

export const usePrepareManualAnchorPlacement = () => {
    const { t } = useTranslation();
    const solveAnchorPlacementMutation = useSolveAnchorPlacement();
    const [searchParams] = useSearchParams();
    const sessionId = useMemo(() => searchParams.get('formingsession') ?? EMPTY_GUID, [searchParams]);

    return useCallback(
        async ({ articleId, poiIndex }: ManualAnchorPlacementParams): Promise<boolean> => {
            const state = usePlacementStore.getState();
            state.setManualPlacementDataLoading(articleId, true);

            try {
                const response = await solveAnchorPlacementMutation.mutateAsync({
                    data: buildSolveAnchorPlacementRequest({
                        state,
                        sessionId,
                        systemId: import.meta.env.VITE_DEFAULT_SYSTEM_ID,
                        poiIndex,
                        selectedAnchorArticleGuid: articleId
                    })
                });
                const isActivated =
                    response.accepted &&
                    activateManualSnapPlacement({
                        state,
                        articleId,
                        poiIndex,
                        settingType: SettingModelSettingType.Anchors,
                        snapPoints: response.snapPoints ?? [],
                        snapRanges: response.snapRanges ?? []
                    });
                if (!isActivated) {
                    state.clearManualPlacementData();
                    toastNotifications.information({
                        title: t('info'),
                        message: response.message || t('errorManualAnchorPlacement')
                    });
                }
                return isActivated;
            } catch (error) {
                state.clearManualPlacementData();
                toastNotifications.error({
                    title: t('error'),
                    message: error instanceof Error ? error.message : t('errorManualAnchorPlacement')
                });
                return false;
            } finally {
                usePlacementStore.setState({ waitForPlacementData: false });
            }
        },
        [sessionId, solveAnchorPlacementMutation, t]
    );
};
